import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  IoAddCircleOutline, IoCreateOutline, IoTrashOutline, IoChevronDown, IoChevronForward,
  IoClose, IoSchoolOutline, IoLayersOutline, IoDocumentTextOutline, IoWarningOutline,
  IoArrowUp, IoArrowDown,
} from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import { BIOMES } from "@/lib/biomes";
import { useAuth } from "@/context/AuthContext";
import {
  listFormations, getFormation, getGate,
  createFormation, updateFormation, deleteFormationApi,
  createGate, updateGate, deleteGateApi,
  createModule, updateModule, deleteModuleApi,
  createLesson, updateLesson, deleteLessonApi,
  listBiblio,
} from "@/lib/api";

/* ---------------- petits composants UI ---------------- */
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.box, maxWidth: wide ? 760 : 520 }} onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div style={m.head}>
          <span style={m.title}>{title}</span>
          <button style={m.x} onClick={onClose} data-testid="admin-modal-close"><IoClose size={20} /></button>
        </div>
        <div style={m.body}>{children}</div>
      </div>
    </div>
  );
}
const Field = ({ label, children, hint }) => (
  <label style={m.field}><span style={m.label}>{label}</span>{children}{hint && <span style={m.hint}>{hint}</span>}</label>
);
const inputStyle = { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" };

function ConfirmDelete({ label, onConfirm, testid }) {
  const [armed, setArmed] = useState(false);
  if (!armed) return <button style={a.iconBtnDanger} title="Supprimer" data-testid={testid} onClick={(e) => { e.stopPropagation(); setArmed(true); }}><IoTrashOutline size={16} /></button>;
  return (
    <span style={{ display: "inline-flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
      <button style={a.confirmBtn} data-testid={`${testid}-confirm`} onClick={() => { setArmed(false); onConfirm(); }}>Confirmer</button>
      <button style={a.cancelMini} onClick={() => setArmed(false)}>Annuler</button>
    </span>
  );
}

/* ---------------- Console admin de l'École ---------------- */
export default function EcoleAdmin() {
  const nav = useNavigate();
  const { loading, isAdmin } = useAuth();
  const [formations, setFormations] = useState(null);
  const [fid, setFid] = useState(null);
  const [formation, setFormation] = useState(null);   // avec gates
  const [openGates, setOpenGates] = useState({});      // gid -> gate detail (modules+lessons)

  // dialogs
  const [fDlg, setFDlg] = useState(null);   // {mode, data}
  const [gDlg, setGDlg] = useState(null);
  const [modDlg, setModDlg] = useState(null);
  const [lesDlg, setLesDlg] = useState(null);

  useEffect(() => {
    if (!loading && !isAdmin) nav("/hub", { replace: true });
  }, [loading, isAdmin, nav]);

  const loadFormations = () => listFormations().then((list) => {
    setFormations(list);
    if (list.length && !fid) setFid(list[0].id);
  }).catch(() => setFormations([]));
  useEffect(() => { loadFormations(); }, []); // eslint-disable-line

  const loadFormation = (id) => { if (!id) { setFormation(null); return; } getFormation(id).then(setFormation).catch(() => setFormation(null)); };
  useEffect(() => { loadFormation(fid); setOpenGates({}); }, [fid]);

  const toggleGate = async (g) => {
    if (openGates[g.id]) { const n = { ...openGates }; delete n[g.id]; setOpenGates(n); return; }
    try { const detail = await getGate(g.id); setOpenGates((o) => ({ ...o, [g.id]: detail })); }
    catch { toast.error("Impossible d'ouvrir la porte"); }
  };
  const reloadGate = async (gid) => { try { const d = await getGate(gid); setOpenGates((o) => ({ ...o, [gid]: d })); } catch {} };

  const moveModule = async (gid, idx, dir) => {
    const mods = openGates[gid]?.modules || [];
    const j = idx + dir;
    if (j < 0 || j >= mods.length) return;
    const A = mods[idx], B = mods[j];
    const oA = Number(A.order) || (idx + 1), oB = Number(B.order) || (j + 1);
    try {
      await updateModule(A.id, { gate_id: gid, title: A.title, description: A.description, order: oB });
      await updateModule(B.id, { gate_id: gid, title: B.title, description: B.description, order: oA });
      await reloadGate(gid);
    } catch { toast.error("Échec du déplacement du module"); }
  };

  if (!isAdmin) return null;

  return (
    <DojoShell crumbs={[{ label: "École", to: "/ecole" }, { label: "Mode Création" }]}>
      <div style={a.head}>
        <div>
          <div style={a.eyebrow}>MODE CRÉATION</div>
          <h1 style={a.h1}>Éditeur du Dōjō</h1>
          <p style={a.sub}>Crée et organise tes formations, portes, modules et leçons — sans code.</p>
        </div>
        <button style={a.primary} data-testid="admin-new-formation" onClick={() => setFDlg({ mode: "create", data: {} })}>
          <IoAddCircleOutline size={18} /> Nouvelle formation
        </button>
      </div>

      {/* Sélecteur de formation */}
      {formations === null ? <p style={{ color: "var(--text-secondary)" }}>Chargement…</p> : (
        <div style={a.formTabs}>
          {formations.map((f) => (
            <button key={f.id} data-testid={`admin-formation-tab-${f.id}`} onClick={() => setFid(f.id)}
              style={{ ...a.formTab, ...(f.id === fid ? a.formTabActive : {}) }}>
              {f.kanji || "門"} {f.title}{!f.is_published && <span style={a.draftDot}>brouillon</span>}
            </button>
          ))}
          {formations.length === 0 && <p style={{ color: "var(--text-secondary)" }}>Aucune formation. Crée la première ↗</p>}
        </div>
      )}

      {formation && (
        <div style={a.panel}>
          <div style={a.panelHead}>
            <div style={a.panelTitleRow}>
              <IoSchoolOutline size={20} color={RED} />
              <span style={a.panelTitle}>{formation.title}</span>
              <button style={a.iconBtn} data-testid="admin-edit-formation" title="Modifier la formation" onClick={() => setFDlg({ mode: "edit", data: formation })}><IoCreateOutline size={16} /></button>
              <ConfirmDelete label="formation" testid="admin-delete-formation" onConfirm={async () => {
                await deleteFormationApi(formation.id); toast.success("Formation supprimée");
                setFid(null); await loadFormations(); setFormation(null);
              }} />
            </div>
            <button style={a.addGate} data-testid="admin-add-gate" onClick={() => setGDlg({ mode: "create", data: { formation_id: formation.id, index: (formation.gates?.length || 0) + 1, order: (formation.gates?.length || 0) + 1 } })}>
              <IoAddCircleOutline size={16} /> Ajouter une porte
            </button>
          </div>

          {(formation.gates || []).length === 0 && <p style={{ color: "var(--text-secondary)", padding: "8px 4px" }}>Aucune porte. Ajoute la première porte ↗</p>}

          {(formation.gates || []).map((g) => {
            const open = !!openGates[g.id];
            const detail = openGates[g.id];
            return (
              <div key={g.id} style={a.gate}>
                <div style={a.gateHead} onClick={() => toggleGate(g)} data-testid={`admin-gate-${g.id}`}>
                  <span style={a.chev}>{open ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />}</span>
                  <span style={a.gateKanji}>{g.kanji || "門"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={a.gateTitle}>
                      {g.index}. {g.title}
                    </div>
                    <div style={a.gateMeta}>{g.element} · ordre {g.order}</div>
                  </div>
                  <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", gap: 6 }}>
                    <button style={a.iconBtn} title="Modifier la porte" data-testid={`admin-edit-gate-${g.id}`} onClick={() => setGDlg({ mode: "edit", data: g })}><IoCreateOutline size={16} /></button>
                    <ConfirmDelete testid={`admin-delete-gate-${g.id}`} onConfirm={async () => { await deleteGateApi(g.id); toast.success("Porte supprimée"); loadFormation(fid); }} />
                  </span>
                </div>

                {open && (
                  <div style={a.gateBody}>
                    <button style={a.addModule} data-testid={`admin-add-module-${g.id}`} onClick={() => setModDlg({ mode: "create", gid: g.id, data: { gate_id: g.id, order: (detail?.modules?.length || 0) + 1 } })}>
                      <IoAddCircleOutline size={15} /> Ajouter un module
                    </button>
                    {!detail ? <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>Chargement…</p> :
                      (detail.modules || []).length === 0 ? <p style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 8 }}>Aucun module.</p> :
                      (detail.modules || []).map((mod, mi) => (
                        <div key={mod.id} style={a.module}>
                          <div style={a.moduleHead}>
                            <IoLayersOutline size={16} color="var(--text-secondary)" />
                            <div style={{ flex: 1 }}>
                              <div style={a.moduleTitle}>{mod.title}</div>
                              {mod.description && <div style={a.moduleDesc}>{mod.description}</div>}
                            </div>
                            <div style={a.orderBtns}>
                              <button style={{ ...a.iconBtn, opacity: mi === 0 ? 0.35 : 1 }} disabled={mi === 0} title="Monter" data-testid={`admin-module-up-${mod.id}`} onClick={() => moveModule(g.id, mi, -1)}><IoArrowUp size={14} /></button>
                              <button style={{ ...a.iconBtn, opacity: mi === (detail.modules.length - 1) ? 0.35 : 1 }} disabled={mi === (detail.modules.length - 1)} title="Descendre" data-testid={`admin-module-down-${mod.id}`} onClick={() => moveModule(g.id, mi, 1)}><IoArrowDown size={14} /></button>
                            </div>
                            <button style={a.iconBtn} title="Modifier le module" data-testid={`admin-edit-module-${mod.id}`} onClick={() => setModDlg({ mode: "edit", gid: g.id, data: mod })}><IoCreateOutline size={15} /></button>
                            <ConfirmDelete testid={`admin-delete-module-${mod.id}`} onConfirm={async () => { await deleteModuleApi(mod.id); toast.success("Module supprimé"); reloadGate(g.id); }} />
                          </div>
                          <div style={a.lessons}>
                            {(mod.lessons || []).map((l, i) => (
                              <div key={l.id} style={a.lesson}>
                                <span style={a.lNum}>{i + 1}</span>
                                <IoDocumentTextOutline size={15} color="var(--text-secondary)" />
                                <span style={a.lTitle}>{l.title}</span>
                                <span style={a.lMeta}>{l.youtube_id ? "▶ vidéo" : "— pas de vidéo"} {l.duration ? `· ${l.duration}` : ""}</span>
                                <button style={a.iconBtn} title="Modifier la leçon" data-testid={`admin-edit-lesson-${l.id}`} onClick={() => setLesDlg({ mode: "edit", gid: g.id, data: l })}><IoCreateOutline size={15} /></button>
                                <ConfirmDelete testid={`admin-delete-lesson-${l.id}`} onConfirm={async () => { await deleteLessonApi(l.id); toast.success("Leçon supprimée"); reloadGate(g.id); }} />
                              </div>
                            ))}
                            <button style={a.addLesson} data-testid={`admin-add-lesson-${mod.id}`} onClick={() => setLesDlg({ mode: "create", gid: g.id, data: { module_id: mod.id, order: (mod.lessons?.length || 0) + 1 } })}>
                              <IoAddCircleOutline size={15} /> Ajouter une leçon
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Dialogs ---- */}
      {fDlg && <FormationDialog dlg={fDlg} onClose={() => setFDlg(null)} onSaved={async (saved) => { setFDlg(null); await loadFormations(); setFid(saved.id); loadFormation(saved.id); }} />}
      {gDlg && <GateDialog dlg={gDlg} onClose={() => setGDlg(null)} onSaved={() => { setGDlg(null); loadFormation(fid); }} />}
      {modDlg && <ModuleDialog dlg={modDlg} onClose={() => setModDlg(null)} onSaved={() => { const gid = modDlg.gid; setModDlg(null); reloadGate(gid); }} />}
      {lesDlg && <LessonDialog dlg={lesDlg} onClose={() => setLesDlg(null)} onSaved={() => { const gid = lesDlg.gid; setLesDlg(null); reloadGate(gid); }} />}
    </DojoShell>
  );
}

/* ---------------- Dialogs ---------------- */
function FormationDialog({ dlg, onClose, onSaved }) {
  const edit = dlg.mode === "edit";
  const [d, setD] = useState({ title: "", kanji: "門", description: "", cover: "", order: 0, is_published: true, ...dlg.data });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!d.title.trim()) return toast.error("Le titre est requis");
    setBusy(true);
    try {
      const payload = { title: d.title, kanji: d.kanji, description: d.description, cover: d.cover, order: Number(d.order) || 0, is_published: !!d.is_published };
      const saved = edit ? await updateFormation(d.id, payload) : await createFormation(payload);
      toast.success(edit ? "Formation mise à jour" : "Formation créée");
      onSaved(saved);
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  return (
    <Modal title={edit ? "Modifier la formation" : "Nouvelle formation"} onClose={onClose}>
      <Field label="Titre"><input style={inputStyle} data-testid="fdlg-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Le Chemin des 7 Portes" /></Field>
      <div style={m.row}>
        <Field label="Kanji"><input style={inputStyle} data-testid="fdlg-kanji" value={d.kanji} onChange={(e) => setD({ ...d, kanji: e.target.value })} /></Field>
        <Field label="Ordre"><input style={inputStyle} type="number" data-testid="fdlg-order" value={d.order} onChange={(e) => setD({ ...d, order: e.target.value })} /></Field>
      </div>
      <Field label="Description"><textarea style={{ ...inputStyle, resize: "vertical" }} rows={3} data-testid="fdlg-desc" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} /></Field>
      <ImageUpload label="Image de couverture" testid="fdlg-cover" value={d.cover} onChange={(v) => setD({ ...d, cover: v })} />
      <label style={m.check}><input type="checkbox" data-testid="fdlg-published" checked={!!d.is_published} onChange={(e) => setD({ ...d, is_published: e.target.checked })} /> Publiée (visible des élèves)</label>
      <ModalActions busy={busy} onClose={onClose} onSave={save} testid="fdlg" />
    </Modal>
  );
}

function GateDialog({ dlg, onClose, onSaved }) {
  const edit = dlg.mode === "edit";
  const [d, setD] = useState({ title: "", kanji: "", element: "", description: "", cover: "", badge_label: "", badge_kanji: "", index: 1, order: 0, is_gatekeeper: false, biome: null, ...dlg.data });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!d.title.trim()) return toast.error("Le titre est requis");
    setBusy(true);
    try {
      const payload = { formation_id: d.formation_id, index: Number(d.index) || 1, title: d.title, kanji: d.kanji, element: d.element, description: d.description, cover: d.cover, badge_label: d.badge_label, badge_kanji: d.badge_kanji, order: Number(d.order) || 0, is_gatekeeper: !!d.is_gatekeeper, biome: d.biome ? Number(d.biome) : null };
      if (edit) await updateGate(d.id, payload); else await createGate(payload);
      toast.success(edit ? "Porte mise à jour" : "Porte créée");
      onSaved();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const activeBiome = d.biome ? Number(d.biome) : (Number(d.index) || 1);
  return (
    <Modal title={edit ? "Modifier la porte" : "Nouvelle porte"} onClose={onClose}>
      <Field label="Titre"><input style={inputStyle} data-testid="gdlg-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Porte du Feu" /></Field>
      <div style={m.row}>
        <Field label="Kanji"><input style={inputStyle} data-testid="gdlg-kanji" value={d.kanji} onChange={(e) => setD({ ...d, kanji: e.target.value })} placeholder="火" /></Field>
        <Field label="Élément / thème"><input style={inputStyle} data-testid="gdlg-element" value={d.element} onChange={(e) => setD({ ...d, element: e.target.value })} placeholder="L'élan" /></Field>
      </div>
      <div style={m.row}>
        <Field label="N° (index)"><input style={inputStyle} type="number" data-testid="gdlg-index" value={d.index} onChange={(e) => setD({ ...d, index: e.target.value })} /></Field>
        <Field label="Ordre"><input style={inputStyle} type="number" data-testid="gdlg-order" value={d.order} onChange={(e) => setD({ ...d, order: e.target.value })} /></Field>
      </div>
      <Field label="Description"><textarea style={{ ...inputStyle, resize: "vertical" }} rows={2} data-testid="gdlg-desc" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} /></Field>
      <ImageUpload label="Image de couverture" testid="gdlg-cover" value={d.cover} onChange={(v) => setD({ ...d, cover: v })} />
      <div style={m.row}>
        <Field label="Libellé du sceau (badge)"><input style={inputStyle} data-testid="gdlg-badge-label" value={d.badge_label} onChange={(e) => setD({ ...d, badge_label: e.target.value })} placeholder="Sceau du Feu" /></Field>
        <Field label="Kanji du sceau"><input style={inputStyle} data-testid="gdlg-badge-kanji" value={d.badge_kanji} onChange={(e) => setD({ ...d, badge_kanji: e.target.value })} placeholder="火" /></Field>
      </div>
      <Field label="Ambiance de la porte (couleur & animation)" hint="Choisis le thème visuel de cette porte, indépendamment de son ordre.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="gdlg-biome">
          {Object.entries(BIOMES).map(([key, bm]) => {
            const on = activeBiome === Number(key);
            return (
              <button key={key} type="button" data-testid={`gdlg-biome-${key}`} onClick={() => setD({ ...d, biome: Number(key) })}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 84, padding: "10px 6px", borderRadius: 12, cursor: "pointer",
                  border: on ? `2.5px solid ${bm.color}` : "1.5px solid var(--border-c)", background: on ? `${bm.color}1a` : "var(--bg-tertiary)" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: bm.env, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: `0 0 12px ${bm.color}88` }}>{bm.motif}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-primary)" }}>{bm.name}</span>
              </button>
            );
          })}
        </div>
      </Field>
      <ModalActions busy={busy} onClose={onClose} onSave={save} testid="gdlg" />
    </Modal>
  );
}

function ModuleDialog({ dlg, onClose, onSaved }) {
  const edit = dlg.mode === "edit";
  const [d, setD] = useState({ title: "", description: "", order: 0, resource_ids: [], ...dlg.data });
  const [busy, setBusy] = useState(false);
  const [biblio, setBiblio] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { listBiblio().then((r) => setBiblio(r || [])).catch(() => {}); }, []);
  const selected = d.resource_ids || [];
  const toggle = (id) => setD((s) => { const cur = s.resource_ids || []; return { ...s, resource_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });
  const filtered = biblio.filter((r) => !q.trim() || (r.title || "").toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    if (!d.title.trim()) return toast.error("Le titre est requis");
    setBusy(true);
    try {
      const payload = { gate_id: d.gate_id, title: d.title, description: d.description, order: Number(d.order) || 0, resource_ids: d.resource_ids || [] };
      if (edit) await updateModule(d.id, payload); else await createModule(payload);
      toast.success(edit ? "Module mis à jour" : "Module créé");
      onSaved();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  return (
    <Modal title={edit ? "Modifier le module" : "Nouveau module"} onClose={onClose}>
      <Field label="Titre"><input style={inputStyle} data-testid="moddlg-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Module 1 — Les bases" /></Field>
      <Field label="Ordre"><input style={inputStyle} type="number" data-testid="moddlg-order" value={d.order} onChange={(e) => setD({ ...d, order: e.target.value })} /></Field>
      <Field label="Description"><textarea style={{ ...inputStyle, resize: "vertical" }} rows={2} data-testid="moddlg-desc" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} /></Field>
      <Field label={`Ressources liées de la Bibliothèque (${selected.length} sélectionnée${selected.length > 1 ? "s" : ""})`} hint="Ces fiches s'afficheront dans le module pour aider à comprendre / faire l'exercice.">
        <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Rechercher une fiche…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="moddlg-resource-search" />
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border-c)", borderRadius: 10 }} data-testid="moddlg-resource-list">
          {filtered.length === 0 && <div style={{ padding: 14, fontSize: 13, color: "var(--text-secondary)" }}>Aucune fiche. Ajoute des ressources dans la Bibliothèque.</div>}
          {filtered.map((r) => {
            const on = selected.includes(r.id);
            return (
              <label key={r.id} data-testid={`moddlg-resource-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: "1px solid var(--border-c)", cursor: "pointer", background: on ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "transparent" }}>
                <input type="checkbox" checked={on} onChange={() => toggle(r.id)} style={{ accentColor: RED }} />
                <span style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text-primary)", flex: 1 }}>{r.title}</span>
                <span style={{ fontSize: 11.5, color: "var(--text-secondary)", fontWeight: 700 }}>{r.type} · P{r.porte_index}</span>
              </label>
            );
          })}
        </div>
      </Field>
      <ModalActions busy={busy} onClose={onClose} onSave={save} testid="moddlg" />
    </Modal>
  );
}

function LessonDialog({ dlg, onClose, onSaved }) {
  const edit = dlg.mode === "edit";
  const [d, setD] = useState({ title: "", youtube_id: "", duration: "", order: 0, rich_content: "", exercise: "", exercise_type: "paragraph", qcm_options: [], qcm_multi: false, ...dlg.data });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const save = async () => {
    if (!d.title.trim()) return toast.error("Le titre est requis");
    setBusy(true);
    try {
      const payload = { module_id: d.module_id, title: d.title, youtube_id: (d.youtube_id || "").trim(), duration: d.duration, order: Number(d.order) || 0, rich_content: d.rich_content || "", exercise: d.exercise || "", resources: d.resources || [], exercise_type: d.exercise_type || "paragraph", qcm_options: (d.exercise_type === "qcm" ? (d.qcm_options || []).filter((o) => (o.text || "").trim()) : []), qcm_multi: !!d.qcm_multi };
      if (edit) await updateLesson(d.id, payload); else await createLesson(payload);
      toast.success(edit ? "Leçon mise à jour" : "Leçon créée");
      onSaved();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const ytSrc = d.youtube_id ? `https://www.youtube-nocookie.com/embed/${d.youtube_id.trim()}?rel=0&modestbranding=1` : null;
  return (
    <Modal title={edit ? "Modifier la leçon" : "Nouvelle leçon"} onClose={onClose} wide>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={lp.previewToggle} data-testid="lesdlg-preview-toggle" onClick={() => setPreview((v) => !v)}>
          {preview ? "✎ Revenir à l'édition" : "👁 Aperçu vue élève"}
        </button>
      </div>
      {preview ? (
        <div style={lp.wrap} data-testid="lesson-preview">
          <h2 style={lp.title}>{d.title || "Titre de la leçon"}</h2>
          <div style={lp.player}>
            {ytSrc ? <iframe title="preview" src={ytSrc} style={lp.iframe} frameBorder="0" allowFullScreen />
              : <div style={lp.noVid}>🔒 Aucune vidéo (ID YouTube vide)</div>}
          </div>
          {d.rich_content && (<><div style={lp.h}>Description</div><div style={lp.rich} dangerouslySetInnerHTML={{ __html: d.rich_content }} /></>)}
          {d.exercise && (<><div style={lp.h}>🎯 Ton exercice</div><div style={lp.rich} dangerouslySetInnerHTML={{ __html: d.exercise }} /></>)}
        </div>
      ) : (
        <>
          <Field label="Titre"><input style={inputStyle} data-testid="lesdlg-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Leçon 1 — Retrouver l'élan" /></Field>
          <div style={m.row}>
            <Field label="ID YouTube" hint="Ex : pour youtube.com/watch?v=M7lc1UVf-VE → M7lc1UVf-VE"><input style={inputStyle} data-testid="lesdlg-youtube" value={d.youtube_id} onChange={(e) => setD({ ...d, youtube_id: e.target.value })} placeholder="M7lc1UVf-VE" /></Field>
            <Field label="Durée"><input style={inputStyle} data-testid="lesdlg-duration" value={d.duration} onChange={(e) => setD({ ...d, duration: e.target.value })} placeholder="8 min" /></Field>
            <Field label="Ordre"><input style={inputStyle} type="number" data-testid="lesdlg-order" value={d.order} onChange={(e) => setD({ ...d, order: e.target.value })} /></Field>
          </div>
          <div style={m.field}><span style={m.label}>Description (contenu de la leçon)</span>
            <RichTextEditor value={d.rich_content} onChange={(v) => setD({ ...d, rich_content: v })} placeholder="Décris la leçon…" />
          </div>
          <div style={m.field}><span style={m.label}>Exercice — consigne</span>
            <RichTextEditor value={d.exercise} onChange={(v) => setD({ ...d, exercise: v })} placeholder="Consigne de l'exercice…" />
          </div>
          <Field label="Type d'exercice">
            <select style={inputStyle} data-testid="lesdlg-extype" value={d.exercise_type || "paragraph"} onChange={(e) => setD({ ...d, exercise_type: e.target.value })}>
              <option value="paragraph">Paragraphe (réponse écrite)</option>
              <option value="qcm">QCM (choix multiple)</option>
              <option value="file">Upload de fichier (PDF, texte…)</option>
            </select>
          </Field>
          {d.exercise_type === "qcm" && (
            <div style={m.field}>
              <label style={{ ...m.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!d.qcm_multi} onChange={(e) => setD({ ...d, qcm_multi: e.target.checked })} data-testid="lesdlg-qcm-multi" /> Plusieurs bonnes réponses possibles
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                {(d.qcm_options || []).map((o, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid={`lesdlg-qcm-row-${i}`}>
                    <input type="checkbox" title="Bonne réponse" checked={!!o.correct} data-testid={`lesdlg-qcm-correct-${i}`}
                      onChange={(e) => { const opts = [...d.qcm_options]; opts[i] = { ...opts[i], correct: e.target.checked }; setD({ ...d, qcm_options: opts }); }} />
                    <input style={{ ...inputStyle, flex: 1 }} value={o.text || ""} placeholder={`Réponse ${i + 1}`} data-testid={`lesdlg-qcm-text-${i}`}
                      onChange={(e) => { const opts = [...d.qcm_options]; opts[i] = { ...opts[i], text: e.target.value }; setD({ ...d, qcm_options: opts }); }} />
                    <button style={{ border: "none", background: "transparent", color: RED, cursor: "pointer", fontWeight: 900 }} onClick={() => setD({ ...d, qcm_options: d.qcm_options.filter((_, x) => x !== i) })}>×</button>
                  </div>
                ))}
                <button style={{ ...lp.previewToggle, alignSelf: "flex-start" }} data-testid="lesdlg-qcm-add" onClick={() => setD({ ...d, qcm_options: [...(d.qcm_options || []), { text: "", correct: false }] })}>+ Ajouter une réponse</button>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Coche la (ou les) bonne(s) réponse(s).</span>
              </div>
            </div>
          )}
        </>
      )}
      <ModalActions busy={busy} onClose={onClose} onSave={save} testid="lesdlg" />
    </Modal>
  );
}

const lp = {
  previewToggle: { padding: "7px 14px", borderRadius: 999, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 12.5, cursor: "pointer" },
  wrap: { border: "1px solid #eee", borderRadius: 12, padding: 18, background: "var(--bg-tertiary)" },
  title: { fontSize: 22, fontWeight: 900, margin: "0 0 12px" },
  player: { position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "var(--text-primary)", marginBottom: 16 },
  iframe: { position: "absolute", inset: 0, width: "100%", height: "100%" },
  noVid: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 },
  h: { fontWeight: 800, fontSize: 14, margin: "14px 0 6px" },
  rich: { fontSize: 14.5, color: "var(--text-secondary)", lineHeight: 1.7 },
};

function ModalActions({ busy, onClose, onSave, testid }) {
  return (
    <div style={m.actions}>
      <button style={m.cancel} onClick={onClose} data-testid={`${testid}-cancel`}>Annuler</button>
      <button style={{ ...m.save, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={onSave} data-testid={`${testid}-save`}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
    </div>
  );
}

/* ---------------- styles ---------------- */
const a = {
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, marginBottom: 22, flexWrap: "wrap" },
  eyebrow: { color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 2 },
  h1: { fontSize: 30, fontWeight: 900, margin: "4px 0 2px" },
  sub: { color: "var(--text-secondary)", fontSize: 14, margin: 0 },
  primary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 11, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: `0 8px 20px color-mix(in srgb, var(--brand) 27%, transparent)` },
  formTabs: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 },
  formTab: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 999, border: "1.5px solid #e5e5e5", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  formTabActive: { borderColor: RED, background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: RED },
  draftDot: { marginLeft: 6, fontSize: 10, fontWeight: 700, color: "var(--text-secondary)", background: "var(--border-c)", padding: "1px 6px", borderRadius: 999 },
  panel: { border: "1px solid #eee", borderRadius: 18, background: "var(--bg-secondary)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", padding: "18px 20px" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" },
  panelTitleRow: { display: "flex", alignItems: "center", gap: 10 },
  panelTitle: { fontSize: 19, fontWeight: 900 },
  addGate: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  gate: { border: "1px solid #eee", borderRadius: 14, marginBottom: 12, overflow: "hidden", background: "var(--bg-secondary)" },
  gateHead: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer", background: "var(--bg-tertiary)" },
  chev: { color: "var(--text-secondary)", display: "flex" },
  gateKanji: { fontSize: 28, fontWeight: 800, color: RED, width: 34, textAlign: "center" },
  gateTitle: { fontWeight: 800, fontSize: 15.5, display: "flex", alignItems: "center", gap: 8 },
  gateMeta: { fontSize: 12.5, color: "var(--text-secondary)" },
  tagGk: { fontSize: 10, fontWeight: 700, color: RED, background: "color-mix(in srgb, var(--brand) 10%, transparent)", padding: "1px 7px", borderRadius: 999 },
  gateBody: { padding: "8px 16px 16px 46px", background: "var(--bg-secondary)" },
  addModule: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9, border: "1.5px dashed #ccc", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12.5, cursor: "pointer", marginBottom: 10 },
  module: { border: "1px solid #f0f0f0", borderRadius: 12, marginBottom: 10, overflow: "hidden" },
  moduleHead: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--bg-tertiary)" },
  moduleTitle: { fontWeight: 800, fontSize: 14 },
  moduleDesc: { fontSize: 12, color: "var(--text-secondary)" },
  lessons: { padding: "6px 10px 10px", display: "flex", flexDirection: "column", gap: 4 },
  lesson: { display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 9, background: "var(--bg-secondary)", border: "1px solid #f4f4f4" },
  lNum: { width: 22, height: 22, borderRadius: "50%", background: "var(--border-c)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, flexShrink: 0 },
  lTitle: { flex: 1, fontWeight: 700, fontSize: 13.5 },
  lMeta: { fontSize: 11.5, color: "var(--text-secondary)" },
  addLesson: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9, border: "1.5px dashed #ddd", background: "var(--bg-secondary)", color: RED, fontWeight: 700, fontSize: 12.5, cursor: "pointer", alignSelf: "flex-start", marginTop: 4 },
  iconBtn: { width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #e5e5e5", background: "var(--bg-secondary)", color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0 },
  orderBtns: { display: "inline-flex", gap: 4, flexShrink: 0 },
  iconBtnDanger: { width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #f3d0d0", background: "var(--bg-secondary)", color: "#c0392b", cursor: "pointer", flexShrink: 0 },
  confirmBtn: { padding: "6px 10px", borderRadius: 8, border: "none", background: "#c0392b", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" },
  cancelMini: { padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12, cursor: "pointer" },
};
const m = {
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto", backdropFilter: "blur(3px)" },
  box: { width: "100%", background: "var(--bg-secondary)", borderRadius: 18, boxShadow: "0 30px 80px rgba(0,0,0,0.35)", overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f0f0f0" },
  title: { fontSize: 17, fontWeight: 900 },
  x: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid #eee", background: "var(--bg-secondary)", cursor: "pointer", color: "var(--text-secondary)" },
  body: { padding: 20, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6, flex: 1 },
  label: { fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)" },
  hint: { fontSize: 11.5, color: "var(--text-secondary)" },
  row: { display: "flex", gap: 12 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 600, color: "var(--text-secondary)", cursor: "pointer" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid #e5e5e5", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer", fontSize: 14 },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 14, boxShadow: `0 6px 16px color-mix(in srgb, var(--brand) 27%, transparent)` },
};
